/**
 * Каталог шаблонов.
 *
 * Шаблон — типизированная конфигурация расположения и стиля. Здесь нет и не
 * может быть произвольного HTML, SVG или JavaScript: рендерер знает только
 * перечисленные ниже поля.
 *
 * Все декоративные элементы (рамка, плашка, полоса) размещаются строго за
 * пределами защитного поля QR — за это отвечает расчёт в core/layout.
 */
export type TextSlot = 'title' | 'caption' | 'note';

export type TemplateBlock = { kind: 'qr' } | { kind: TextSlot };

export type Template = {
  id: string;
  version: number;
  name: string;
  description: string;
  /** Направление основного потока блоков. */
  direction: 'column' | 'row';
  blocks: TemplateBlock[];
  /** Рамка вокруг всей карточки. */
  frame: { width: number; radius: number } | null;
  /** Контрастная плашка под одним из текстовых блоков. */
  plate: { target: TextSlot; radius: number } | null;
  /** Цветная полоса-акцент по краю карточки. */
  accentBar: { position: 'top' | 'bottom' | 'left'; thickness: number } | null;
  /** Скругление углов карточки. */
  cardRadius: number;
  /** Внешние отступы карточки, доля от ширины документа. */
  padding: number;
  /** Расстояние между блоками, доля от ширины документа. */
  gap: number;
  /** Кегль относительно кегля подписи. */
  titleScale: number;
  noteScale: number;
  /** Доля ширины под QR в горизонтальной раскладке. */
  qrShare: number;
};

export const TEMPLATES: Template[] = [
  {
    id: 'plain',
    version: 1,
    name: 'Без оформления',
    description: 'Только QR и защитное поле. Самый предсказуемый вариант.',
    direction: 'column',
    blocks: [{ kind: 'qr' }],
    frame: null,
    plate: null,
    accentBar: null,
    cardRadius: 0,
    padding: 0.04,
    gap: 0,
    titleScale: 1.6,
    noteScale: 0.8,
    qrShare: 1,
  },
  {
    id: 'caption-bottom',
    version: 1,
    name: 'Подпись снизу',
    description: 'QR и одна строка подписи под ним.',
    direction: 'column',
    blocks: [{ kind: 'qr' }, { kind: 'caption' }],
    frame: null,
    plate: null,
    accentBar: null,
    cardRadius: 0,
    padding: 0.06,
    gap: 0.05,
    titleScale: 1.6,
    noteScale: 0.8,
    qrShare: 1,
  },
  {
    id: 'title-top',
    version: 1,
    name: 'Заголовок сверху',
    description: 'Крупный заголовок над QR.',
    direction: 'column',
    blocks: [{ kind: 'title' }, { kind: 'qr' }],
    frame: null,
    plate: null,
    accentBar: null,
    cardRadius: 0,
    padding: 0.06,
    gap: 0.05,
    titleScale: 1.5,
    noteScale: 0.8,
    qrShare: 1,
  },
  {
    id: 'frame',
    version: 1,
    name: 'Рамка',
    description: 'Тонкая рамка вокруг карточки с подписью снизу.',
    direction: 'column',
    blocks: [{ kind: 'qr' }, { kind: 'caption' }],
    frame: { width: 0.012, radius: 0.04 },
    plate: null,
    accentBar: null,
    cardRadius: 0.04,
    padding: 0.07,
    gap: 0.05,
    titleScale: 1.6,
    noteScale: 0.8,
    qrShare: 1,
  },
  {
    id: 'label',
    version: 1,
    name: 'Плашка',
    description: 'Контрастная плашка с подписью под полем QR.',
    direction: 'column',
    blocks: [{ kind: 'qr' }, { kind: 'caption' }],
    frame: null,
    plate: { target: 'caption', radius: 0.03 },
    accentBar: null,
    cardRadius: 0.04,
    padding: 0.05,
    gap: 0.05,
    titleScale: 1.6,
    noteScale: 0.8,
    qrShare: 1,
  },
  {
    id: 'card',
    version: 1,
    name: 'Карточка',
    description: 'Заголовок, QR и пояснение в вертикальной карточке.',
    direction: 'column',
    blocks: [{ kind: 'title' }, { kind: 'qr' }, { kind: 'caption' }, { kind: 'note' }],
    frame: { width: 0.008, radius: 0.045 },
    plate: null,
    accentBar: null,
    cardRadius: 0.045,
    padding: 0.07,
    gap: 0.04,
    titleScale: 1.45,
    noteScale: 0.72,
    qrShare: 1,
  },
  {
    id: 'horizontal',
    version: 1,
    name: 'Горизонтальная',
    description: 'QR слева, текст справа.',
    direction: 'row',
    blocks: [{ kind: 'qr' }, { kind: 'title' }, { kind: 'caption' }, { kind: 'note' }],
    frame: null,
    plate: null,
    accentBar: null,
    cardRadius: 0.03,
    padding: 0.045,
    gap: 0.04,
    titleScale: 1.3,
    noteScale: 0.72,
    qrShare: 0.52,
  },
  {
    id: 'brand',
    version: 1,
    name: 'Брендовая',
    description: 'Цветная полоса-акцент, рамка и заголовок.',
    direction: 'column',
    blocks: [{ kind: 'title' }, { kind: 'qr' }, { kind: 'caption' }],
    frame: { width: 0.01, radius: 0.04 },
    plate: null,
    accentBar: { position: 'top', thickness: 0.035 },
    cardRadius: 0.04,
    padding: 0.07,
    gap: 0.045,
    titleScale: 1.5,
    noteScale: 0.8,
    qrShare: 1,
  },
];

export const DEFAULT_TEMPLATE_ID = 'caption-bottom';

export function getTemplate(id: string): Template {
  return TEMPLATES.find((t) => t.id === id) ?? TEMPLATES[0]!;
}

export function isKnownTemplateId(id: string): boolean {
  return TEMPLATES.some((t) => t.id === id);
}

export function templateUsesSlot(template: Template, slot: TextSlot): boolean {
  return template.blocks.some((b) => b.kind === slot);
}
